// Flow API Service
// Handles fetching flow data from the database API

import axios from './authService';
import { API_ENDPOINTS } from '../config/apiConfig';

// Market ID mapping
const MARKET_ID_MAP = {
  US: 1,
  CA: 2,
  DE: 3,
  UK: 4,
  AU: 5,
  playground: 6,
};

// Reverse mapping for converting IDs to codes
const ID_TO_MARKET_MAP = {
  1: 'US',
  2: 'CA',
  3: 'DE',
  4: 'UK',
  5: 'AU',
  6: 'playground',
};

/**
 * Get market database ID from market code
 */
export const getMarketId = (marketCode) => {
  return MARKET_ID_MAP[marketCode] || 6; // Default to playground
};

/**
 * Get market code from database ID
 */
export const getMarketCode = (marketId) => {
  return ID_TO_MARKET_MAP[marketId] || 'playground';
};

/**
 * Get all markets as an array of { id, name } objects
 */
export const ALL_MARKETS_FALLBACK = Object.entries(MARKET_ID_MAP).map(([name, id]) => ({
  id,
  name,
}));

/**
 * Fetch all flows from database
 */
export const fetchAllFlows = async () => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.GET_TEST_FLOWS}?table=test_flows`);

    if (response.data && response.data.data) {
      // Transform API flows to local structure
      return response.data.data.map((flow) => ({
        flow_id: flow.id,
        flow_db_id: flow.id,
        flow_name: flow.name,
        flow_description: flow.description,
        test_case_id: flow.test_case_id || `DB-${flow.id}`, // Use database value or fallback to generated
        market: getMarketCode(flow.market_id),
        market_id: flow.market_id,
        is_active: true,
        isFromDatabase: true,
        events: [], // Events will be loaded when editing
      }));
    }

    return [];
  } catch (error) {
    console.error('Error fetching flows from database:', error);
    return [];
  }
};

/**
 * Fetch detailed flow data including events, scenarios, and attributes
 */
export const fetchFlowDetails = async (flowId) => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.GET_TEST_FLOW_DATA}?id=${flowId}`);

    if (response.data && response.data.data) {
      // Transform events from API to local structure
      const events = response.data.data.map((apiEvent) => {
        // Separate inbound and outbound scenarios
        const inboundScenarios = [];
        const outboundScenarios = [];

        if (apiEvent.scenarios) {
          apiEvent.scenarios.forEach((scenario) => {
            // Skip null or undefined scenarios from incomplete database records
            if (!scenario) {
              console.warn('Skipping null scenario in event:', apiEvent.test_event_id);
              return;
            }

            // Create unique scenario_id by prefixing with type to avoid collisions
            const transformedScenario = {
              scenario_id: scenario.test_events_inbound_id
                ? `inbound_${scenario.test_events_inbound_id}`
                : scenario.test_events_outbound_id
                  ? `outbound_${scenario.test_events_outbound_id}`
                  : null,
              scenario_name: scenario.scenario_name,
              scenario_type: scenario.scenario_type,
              description: scenario.description || '',
              matching_attribute_name: scenario.matching_attribute_name || '',
              matching_attribute_value: scenario.matching_attribute_value || '',
              // Scenario-level link to a confluence schema (new-schema scenarios).
              // Authoritative signal that this scenario/event is schema-derived.
              confluence_validation_schema_id: scenario.confluence_validation_schema_id ?? null,
              scenario_attributes: [],
            };

            // Transform attributes if they exist
            if (scenario.scenario_attributes && Array.isArray(scenario.scenario_attributes)) {
              transformedScenario.scenario_attributes = scenario.scenario_attributes.map(
                (attr) => ({
                  attribute_id:
                    attr.test_event_inbound_details_id ||
                    attr.test_event_outbound_details_id ||
                    attr.confluence_validation_schema_value_id,
                  confluence_validation_schema_value_id: attr.confluence_validation_schema_value_id,
                  attribute_db_id:
                    attr.test_event_inbound_details_id || attr.test_event_outbound_details_id,
                  is_confluence_validation_schema_value: attr.confluence_validation_schema_value_id
                    ? true
                    : false,
                  criticality: attr.criticality ?? null,
                  attribute_name: attr.name,
                  expected_value: attr.expected || '', // DB field 'expected' maps to 'expected_value'
                  // Normalize attribute type to proper case (Static/Dynamic)
                  // DB field 'config_type' maps to 'attribute_type'
                  attribute_type: attr.config_type
                    ? attr.config_type.charAt(0).toUpperCase() +
                      attr.config_type.slice(1).toLowerCase()
                    : 'Static', // Default to Static if not specified
                }),
              );
            }

            // Categorize by scenario type
            if (scenario.scenario_type === 'inbound') {
              transformedScenario.scenario_db_id = scenario.test_events_inbound_id;
              inboundScenarios.push(transformedScenario);
            } else if (scenario.scenario_type && scenario.scenario_type.startsWith('outbound')) {
              transformedScenario.scenario_db_id = scenario.test_events_outbound_id;
              transformedScenario.outbound_type = scenario.scenario_type.replace('outbound-', '');
              outboundScenarios.push(transformedScenario);
            }
          });
        }

        return {
          event_id: apiEvent.test_event_id,
          event_db_id: apiEvent.test_event_id,
          event_name: apiEvent.event_name || `Event ${apiEvent.sequence}`,
          event_order: apiEvent.sequence,
          description: apiEvent.description || '',
          priority: apiEvent.priority ?? null,
          scenarios: [...inboundScenarios, ...outboundScenarios], // Combined for display
          inboundScenarios,
          outboundScenarios,
          isFromDatabase: true,
        };
      });

      // Sort events by sequence (create new array to avoid mutation)
      const sortedEvents = [...events].sort((a, b) => a.event_order - b.event_order);

      return sortedEvents;
    }

    return [];
  } catch (error) {
    console.error('Error fetching flow details from database:', error);
    return [];
  }
};

/**
 * Filter flows by market database id
 */
export const filterFlowsByMarket = (flows, marketId) => {
  if (marketId === null || marketId === undefined || marketId === '') return [];

  const id = Number(marketId);
  return flows.filter((flow) => Number(flow.market_id) === id);
};

/**
 * Filter devices by market database id
 */
export const filterDevicesByMarket = (devices, marketId) => {
  if (marketId === null || marketId === undefined || marketId === '') return [];

  const id = Number(marketId);
  return devices.filter((device) => Number(device.market_id) === id);
};

/**
 * Merge database flows with local mock flows
 * Database flows take precedence if there are ID conflicts
 */
export const mergeFlows = (databaseFlows, mockFlows) => {
  const flowMap = new Map();

  // Add mock flows first
  mockFlows.forEach((flow) => {
    if (!flow.isFromDatabase) {
      flowMap.set(flow.flow_id, flow);
    }
  });

  // Add/override with database flows
  databaseFlows.forEach((flow) => {
    flowMap.set(flow.flow_id, flow);
  });

  return Array.from(flowMap.values());
};

/**
 * Save dynamic schema-attribute overrides for new-schema scenarios.
 * `batch` is an array of { direction, scenario_id, confluence_validation_schema_value_id, value }.
 * The whole batch is transactional on the backend.
 */
export const saveSchemaCustomValues = async (batch) => {
  const response = await axios.post(API_ENDPOINTS.SAVE_CUSTOM_VALUES, batch);
  return response.data;
};
